import { PageData } from '@wix/platform-editor-sdk';
import { TpaPageId } from '@wix/pricing-plans-router-utils';
import type { ControllerParams } from '@wix/yoshi-flow-editor';
import { PRICING_PLANS_APP_DEF_ID } from '../constants';

type WixCodeApi = ControllerParams['flowAPI']['controllerConfig']['wixCodeApi'];
type AppParams = ControllerParams['flowAPI']['controllerConfig']['appParams'];

export interface PageInstallationInfo {
  tpaPageId: TpaPageId;
  pageData?: Partial<PageData>;
  titleKey?: string;
}

export const MPA_PAGES: PageInstallationInfo[] = [
  {
    tpaPageId: TpaPageId.PackagePicker,
    pageData: { indexable: true, hidePage: false },
  },
  {
    tpaPageId: TpaPageId.Checkout,
    pageData: { hidePage: true },
  },
  {
    tpaPageId: TpaPageId.ThankYou,
    pageData: { hidePage: true },
  },
  {
    tpaPageId: TpaPageId.Paywall,
    pageData: { indexable: false, hidePage: true },
  },
];

export const MPA_ON_ECOM_PAGES: PageInstallationInfo[] = [
  {
    tpaPageId: TpaPageId.PackagePicker,
    pageData: { indexable: true, hidePage: false },
  },
  {
    tpaPageId: TpaPageId.PlanCustomization,
    pageData: { indexable: true, hidePage: true },
    titleKey: 'plan-customization.pages-panel.title',
  },
  {
    tpaPageId: TpaPageId.ThankYou,
    pageData: { title: 'Continue', hidePage: true },
  },
  {
    tpaPageId: TpaPageId.Paywall,
    pageData: { indexable: false, hidePage: true },
  },
];

export async function isMultiPageApp(wixCodeApi: WixCodeApi, appParams: AppParams): Promise<boolean> {
  if (appParams.appRouters?.length !== 1) {
    return false;
  }
  return (await Promise.all([isMpaPagesInstalled(wixCodeApi), isMpaEcomPagesInstalled(wixCodeApi)])).some(
    (result) => result,
  );
}

export async function getInstallationState(
  wixCodeApi: WixCodeApi,
  appParams: AppParams,
): Promise<{ hasRouter: boolean; isMpaPagesInstalled: boolean; isMpaEcomPagesInstalled: boolean }> {
  const [isMpa, isEcom] = await Promise.all([isMpaPagesInstalled(wixCodeApi), isMpaEcomPagesInstalled(wixCodeApi)]);
  return {
    hasRouter: appParams.appRouters?.length === 1,
    isMpaPagesInstalled: isMpa,
    isMpaEcomPagesInstalled: isEcom,
  };
}

async function isMpaPagesInstalled(wixCodeApi: WixCodeApi): Promise<boolean> {
  return (
    await Promise.all(
      MPA_PAGES.map((page) =>
        wixCodeApi.site.isAppSectionInstalled({
          sectionId: page.tpaPageId,
          appDefinitionId: PRICING_PLANS_APP_DEF_ID,
        }),
      ),
    )
  ).every((section) => section);
}

async function isMpaEcomPagesInstalled(wixCodeApi: WixCodeApi): Promise<boolean> {
  return (
    await Promise.all(
      MPA_ON_ECOM_PAGES.map((page) =>
        wixCodeApi.site.isAppSectionInstalled({
          sectionId: page.tpaPageId,
          appDefinitionId: PRICING_PLANS_APP_DEF_ID,
        }),
      ),
    )
  ).every((section) => section);
}
